import React, { useState, useEffect } from 'react';
import './Workouts.css';

const Workouts = () => {
  // State for workout form
  const [workout, setWorkout] = useState({
    type: 'running',
    duration: '',
    intensity: 'moderate',
    caloriesBurned: '',
    date: new Date().toISOString().substr(0, 10),
    notes: ''
  });

  // State for workout history
  const [workoutHistory, setWorkoutHistory] = useState([]);

  // State for active tab
  const [activeTab, setActiveTab] = useState('log');

  // Load workout data from localStorage on component mount
  useEffect(() => {
    const savedWorkouts = localStorage.getItem('workoutHistory');
    if (savedWorkouts) {
      setWorkoutHistory(JSON.parse(savedWorkouts));
    }
  }, []);

  // Save workout data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('workoutHistory', JSON.stringify(workoutHistory));
  }, [workoutHistory]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWorkout({
      ...workout,
      [name]: value
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    const newWorkout = {
      id: Date.now(), // Use timestamp as unique ID
      ...workout,
      duration: parseInt(workout.duration),
      caloriesBurned: parseInt(workout.caloriesBurned)
    };

    setWorkoutHistory([...workoutHistory, newWorkout]);

    // Reset form
    setWorkout({
      type: 'running',
      duration: '',
      intensity: 'moderate',
      caloriesBurned: '',
      date: new Date().toISOString().substr(0, 10),
      notes: ''
    });
  };

  // Handle workout deletion
  const handleDeleteWorkout = (id) => {
    if (window.confirm('Are you sure you want to delete this workout?')) {
      setWorkoutHistory(workoutHistory.filter(workout => workout.id !== id));
    }
  };

  // Calculate stats for the progress section
  const calculateStats = () => {
    if (workoutHistory.length === 0) return { totalWorkouts: 0, totalCalories: 0, totalDuration: 0, avgCaloriesPerWorkout: 0 };

    const totalWorkouts = workoutHistory.length;
    const totalCalories = workoutHistory.reduce((sum, workout) => sum + workout.caloriesBurned, 0);
    const totalDuration = workoutHistory.reduce((sum, workout) => sum + workout.duration, 0);
    const avgCaloriesPerWorkout = Math.round(totalCalories / totalWorkouts);

    return { totalWorkouts, totalCalories, totalDuration, avgCaloriesPerWorkout };
  };

  // Prepare data for simple visualization
  const prepareSimpleVisualization = () => {
    // Group workouts by date
    const workoutsByDate = {};

    workoutHistory.forEach(workout => {
      if (!workoutsByDate[workout.date]) {
        workoutsByDate[workout.date] = { calories: 0, duration: 0 };
      }
      workoutsByDate[workout.date].calories += workout.caloriesBurned;
      workoutsByDate[workout.date].duration += workout.duration;
    });

    // Convert to array and sort by date
    return Object.keys(workoutsByDate)
      .sort()
      .map(date => ({
        date,
        calories: workoutsByDate[date].calories,
        duration: workoutsByDate[date].duration
      }));
  };

  // Calculate the maximum value for visualizations
  const getMaxValue = (data, key) => {
    return data.reduce((max, item) => Math.max(max, item[key]), 0);
  };

  // Simple bar chart visualization
  const renderSimpleVisualization = () => {
    const data = prepareSimpleVisualization();
    if (data.length === 0) return null;

    const maxCalories = getMaxValue(data, 'calories');
    const maxDuration = getMaxValue(data, 'duration');

    return (
      <div className="simple-charts-container">
        <div className="simple-chart-wrapper">
          <h3>Calories Burned by Date</h3>
          <div className="simple-chart">
            {data.map((item, index) => (
              <div className="chart-bar-container" key={index}>
                <div className="chart-date">{item.date}</div>
                <div className="chart-bar-wrapper">
                  <div
                    className="chart-bar calories-bar"
                    style={{ width: `${(item.calories / maxCalories) * 100}%` }}
                  >
                    <span className="bar-value">{item.calories} kcal</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="simple-chart-wrapper">
          <h3>Workout Duration by Date</h3>
          <div className="simple-chart">
            {data.map((item, index) => (
              <div className="chart-bar-container" key={index}>
                <div className="chart-date">{item.date}</div>
                <div className="chart-bar-wrapper">
                  <div
                    className="chart-bar duration-bar"
                    style={{ width: `${(item.duration / maxDuration) * 100}%` }}
                  >
                    <span className="bar-value">{item.duration} min</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // List of fitness tips with more personalized content
  const fitnessTips = [
    "Stay hydrated! Drink water before, during, and after your workout.",
    "Proper form prevents injuries. If you're unsure, consult a fitness professional.",
    "Rest is crucial for muscle recovery. Aim for 7-8 hours of sleep daily.",
    "Vary your workouts to prevent plateauing and keep yourself motivated.",
    "Start slow and gradually increase intensity to build sustainable habits.",
    "Track your progress regularly to stay motivated and see your improvements.",
    "Consider your nutrition as important as your workouts for optimal results."
  ];

  // Activity breakdown by type
  const renderActivityBreakdown = () => {
    const workoutTypes = {};
    workoutHistory.forEach(workout => {
      if (!workoutTypes[workout.type]) {
        workoutTypes[workout.type] = 0;
      }
      workoutTypes[workout.type] += workout.duration;
    });

    const totalDuration = Object.values(workoutTypes).reduce((sum, duration) => sum + duration, 0);

    return (
      <div className="activity-breakdown">
        <h3>Activity Breakdown</h3>
        <div className="activity-progress-container">
          {Object.entries(workoutTypes).map(([type, duration], index) => {
            const percentage = Math.round((duration / totalDuration) * 100);
            return (
              <div className="activity-type" key={index}>
                <div className="activity-label">
                  <span className="activity-name capitalize">{type}</span>
                  <span className="activity-percentage">{percentage}%</span>
                </div>
                <div className="activity-progress-bar">
                  <div
                    className={`activity-progress activity-${type}`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="activity-duration">{duration} min total</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Get workout trends
  const getWorkoutTrends = () => {
    if (workoutHistory.length < 3) return null;

    // Sort workouts by date
    const sortedWorkouts = [...workoutHistory].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate weekly averages
    const weeklyData = {};
    sortedWorkouts.forEach(workout => {
      const date = new Date(workout.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().substr(0, 10);

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = { workouts: 0, calories: 0, duration: 0 };
      }

      weeklyData[weekKey].workouts += 1;
      weeklyData[weekKey].calories += workout.caloriesBurned;
      weeklyData[weekKey].duration += workout.duration;
    });

    // Convert to array and calculate averages
    const weeklyAverages = Object.keys(weeklyData).map(week => ({
      week,
      avgCalories: Math.round(weeklyData[week].calories / weeklyData[week].workouts),
      avgDuration: Math.round(weeklyData[week].duration / weeklyData[week].workouts),
      totalWorkouts: weeklyData[week].workouts
    }));

    return weeklyAverages;
  };

  // Render workout trends
  const renderWorkoutTrends = () => {
    const trends = getWorkoutTrends();
    if (!trends || trends.length < 2) return null;

    return (
      <div className="workout-trends">
        <h3>Weekly Workout Trends</h3>
        <div className="table-responsive">
          <table className="trends-table">
            <thead>
              <tr>
                <th>Week Starting</th>
                <th>Workouts</th>
                <th>Avg Duration</th>
                <th>Avg Calories</th>
              </tr>
            </thead>
            <tbody>
              {trends.map((week, index) => (
                <tr key={index}>
                  <td>{week.week}</td>
                  <td>{week.totalWorkouts}</td>
                  <td>{week.avgDuration} min</td>
                  <td>{week.avgCalories} kcal</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Export workout data
  const exportWorkoutData = () => {
    const dataStr = JSON.stringify(workoutHistory, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = 'workout-data.json';

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Import workout data
  const importWorkoutData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const importedData = JSON.parse(event.target.result);
        if (Array.isArray(importedData)) {
          // Merge with existing data, avoiding duplicates by ID
          const existingIds = new Set(workoutHistory.map(w => w.id));
          const newWorkouts = importedData.filter(w => !existingIds.has(w.id));

          setWorkoutHistory([...workoutHistory, ...newWorkouts]);
          alert(`Successfully imported ${newWorkouts.length} workouts.`);
        } else {
          alert('Invalid data format. Please upload a valid JSON file.');
        }
      } catch (error) {
        alert('Error parsing file: ' + error.message);
      }
    };
    reader.readAsText(file);

    // Reset the file input
    e.target.value = '';
  };

  // Stats display
  const { totalWorkouts, totalCalories, totalDuration, avgCaloriesPerWorkout } = calculateStats();

  return (
    <div className="workouts-container">
      <h1>Fitness Tracker</h1>

      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'log' ? 'active' : ''}`}
          onClick={() => setActiveTab('log')}
        >
          Log Workout
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Workout History
        </button>
        <button
          className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          Progress
        </button>
        <button
          className={`tab-button ${activeTab === 'tips' ? 'active' : ''}`}
          onClick={() => setActiveTab('tips')}
        >
          Fitness Tips
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'log' && (
          <div className="workout-form-container">
            <h2>Log Your Workout</h2>
            <form onSubmit={handleSubmit} className="workout-form">
              <div className="form-group">
                <label htmlFor="type">Workout Type</label>
                <select
                  id="type"
                  name="type"
                  value={workout.type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="running">Running</option>
                  <option value="cycling">Cycling</option>
                  <option value="weightlifting">Weightlifting</option>
                  <option value="swimming">Swimming</option>
                  <option value="yoga">Yoga</option>
                  <option value="hiking">Hiking</option>
                  <option value="dancing">Dancing</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="duration">Duration (minutes)</label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  value={workout.duration}
                  onChange={handleInputChange}
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="intensity">Intensity</label>
                <select
                  id="intensity"
                  name="intensity"
                  value={workout.intensity}
                  onChange={handleInputChange}
                >
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="caloriesBurned">Calories Burned</label>
                <input
                  type="number"
                  id="caloriesBurned"
                  name="caloriesBurned"
                  value={workout.caloriesBurned}
                  onChange={handleInputChange}
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={workout.date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={workout.notes}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <button type="submit" className="submit-button">Log Workout</button>
            </form>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="workout-history">
            <h2>Workout History</h2>
            {workoutHistory.length === 0 ? (
              <p className="no-data">No workouts logged yet. Start tracking your fitness journey today!</p>
            ) : (
              <>
                <div className="data-controls">
                  <button onClick={exportWorkoutData} className="export-button">Export Data</button>
                  <label className="import-button">
                    Import Data
                    <input
                      type="file"
                      accept=".json"
                      onChange={importWorkoutData}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div className="table-responsive">
                  <table className="workout-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th>Intensity</th>
                        <th>Calories</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workoutHistory.sort((a, b) => new Date(b.date) - new Date(a.date)).map(workout => (
                        <tr key={workout.id}>
                          <td>{workout.date}</td>
                          <td className="capitalize">{workout.type}</td>
                          <td>{workout.duration} min</td>
                          <td className="capitalize">{workout.intensity}</td>
                          <td>{workout.caloriesBurned} kcal</td>
                          <td>{workout.notes}</td>
                          <td>
                            <button
                              onClick={() => handleDeleteWorkout(workout.id)}
                              className="delete-button"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="progress-section">
            <h2>Your Progress</h2>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{totalWorkouts}</div>
                <div className="stat-label">Total Workouts</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totalCalories}</div>
                <div className="stat-label">Total Calories Burned</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totalDuration}</div>
                <div className="stat-label">Total Minutes</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{avgCaloriesPerWorkout}</div>
                <div className="stat-label">Avg Calories/Workout</div>
              </div>
            </div>

            {workoutHistory.length > 0 ? (
              <>
                {renderActivityBreakdown()}
                {renderSimpleVisualization()}
                {renderWorkoutTrends()}
              </>
            ) : (
              <p className="no-data">Log your workouts to see progress charts.</p>
            )}
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="tips-section">
            <h2>Fitness Tips & Recommendations</h2>
            <div className="tips-container">
              {fitnessTips.map((tip, index) => (
                <div className="tip-card" key={index}>
                  <div className="tip-number">{index + 1}</div>
                  <p>{tip}</p>
                </div>
              ))}
            </div>

            {workoutHistory.length > 0 && (
              <div className="personalized-recommendations">
                <h3>Personalized Recommendations</h3>
                <div className="recommendations-container">
                  {workoutHistory.length < 5 ? (
                    <p>Keep logging your workouts! We'll provide personalized recommendations after you've logged more activities.</p>
                  ) : (
                    <>
                      {/* Most frequent workout type */}
                      {(() => {
                        const typeCount = {};
                        workoutHistory.forEach(w => {
                          typeCount[w.type] = (typeCount[w.type] || 0) + 1;
                        });
                        const mostFrequentType = Object.entries(typeCount).sort((a, b) => b[1] - a[1])[0][0];
                        return (
                          <div className="recommendation-card">
                            <h4>Your Favorite Activity</h4>
                            <p>You enjoy <strong>{mostFrequentType}</strong> the most! Consider adding variety with complementary exercises.</p>
                          </div>
                        );
                      })()}

                      {/* Intensity recommendation */}
                      {(() => {
                        const intensities = workoutHistory.map(w => w.intensity);
                        const highIntensityCount = intensities.filter(i => i === 'high').length;
                        const highIntensityPercentage = (highIntensityCount / intensities.length) * 100;

                        return (
                          <div className="recommendation-card">
                            <h4>Intensity Balance</h4>
                            {highIntensityPercentage > 70 ? (
                              <p>Consider adding more recovery and low-intensity workouts to your routine for better balance.</p>
                            ) : highIntensityPercentage < 20 ? (
                              <p>Try adding more high-intensity sessions to challenge yourself and improve fitness.</p>
                            ) : (
                              <p>You have a good balance of workout intensities. Keep it up!</p>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Workouts;